import { slicedToArray as _slicedToArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, Paragraph, Spacer, TextButton } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import API from '@zillow/mortgage-api-client';
import TextField from '../Fields/TextField.js';
import SelectField from '../Fields/SelectField.js';
import { commaSeparateNumber } from '../../utils/zhl-calculator.js';
import DownPaymentField from './DownPaymentField.js';
import PropertyTaxField from './PropertyTaxField.js';
import CheckboxField from '../Fields/CheckboxField.js';
import { FieldConfig, useCustomQuotesURL } from './VALoanCalculator.utils.js';
import LegalPopover from '../CalculatorForm/LegalPopover.js';
import { handleFieldChangeEvent, legalPopoverInteractionEventCmsInfo } from './VALoanCalculator.Clickstream.js';

function VALoanCalculatorFields() {
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    advanced = _useState2[0],
    setAdvanced = _useState2[1];
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      'flex-shrink': '0',
      '@media $lg': {
        'margin-right': '$spacing.md'
      },
      '@media $md': {
        'flex-basis': '30%'
      }
    }
  }, /*#__PURE__*/React.createElement(TextField, {
    label: "Home price",
    name: FieldConfig.homePrice.name,
    leftAdornment: "$",
    config: FieldConfig.homePrice.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    onChange: function onChange() {
      return handleFieldChangeEvent('Home price', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(DownPaymentField, null), /*#__PURE__*/React.createElement(TextField, {
    label: "ZIP code",
    name: FieldConfig.zipCode.name,
    autoComplete: "postal-code",
    onChange: function onChange() {
      return handleFieldChangeEvent('ZIP code', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(SelectField, {
    options: FieldConfig.loanProgram.options,
    label: "Loan program",
    name: FieldConfig.loanProgram.name,
    config: FieldConfig.loanProgram.config,
    autoComplete: "off",
    onChange: function onChange() {
      return handleFieldChangeEvent('Loan program', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "Interest rate",
    rightAdornment: "%",
    name: FieldConfig.interestRate.name,
    config: FieldConfig.interestRate.config,
    textButtonName: "See current rates",
    textButtonHref: CurrentRatesButtonUrl(),
    autoComplete: "off",
    popover: {
      title: 'Interest rate',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Representative interest rates are based upon a national, or state specific average from lenders quoting on Zillow for preliminary research purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics."),
      interactionEventCmsInfo: {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Interest rate'
      }
    },
    onChange: function onChange() {
      return handleFieldChangeEvent('Interest rate', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(Spacer, {
    marginBottom: "xs"
  }, /*#__PURE__*/React.createElement(TextButton, {
    type: "button",
    onClick: yieldCallback(function () {
      setAdvanced(!advanced);
      if (advanced) {
        handleFieldChangeEvent('Simple', 'toggle');
      } else {
        handleFieldChangeEvent('Advanced', 'toggle');
      }
    })
  }, advanced ? 'Simple' : 'Advanced')), advanced && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(CheckboxField, {
    label: "Include taxes/insurance",
    name: FieldConfig.includeTaxesInsurance.name,
    popover: {
      title: 'Taxes and insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues."),
      interactionEventCmsInfo: {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Include taxes/insurance'
      }
    },
    onChange: function onChange() {
      return handleFieldChangeEvent('Include taxes/insurance', 'toggle');
    }
  }), /*#__PURE__*/React.createElement(PropertyTaxField, null), /*#__PURE__*/React.createElement(TextField, {
    label: "Home insurance",
    leftAdornment: "$",
    rightAdornment: "/year",
    name: FieldConfig.homeInsurance.name,
    config: FieldConfig.homeInsurance.config,
    format: commaSeparateNumber,
    autoComplete: "off",
    popover: {
      title: 'Home insurance',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. The VA calculator includes estimated home insurance premiums."),
      interactionEventCmsInfo: {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'Home insurance'
      }
    },
    onChange: function onChange() {
      return handleFieldChangeEvent('Home insurance', 'form_field');
    }
  }), /*#__PURE__*/React.createElement(TextField, {
    label: "HOA dues",
    leftAdornment: "$",
    rightAdornment: "/mo",
    name: FieldConfig.hoa.name,
    config: FieldConfig.hoa.config,
    autoComplete: "off",
    format: commaSeparateNumber,
    popover: {
      title: 'Homeowners association dues',
      body: /*#__PURE__*/React.createElement(Paragraph, null, "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance."),
      interactionEventCmsInfo: {
        block_type_cd: 'tooltip',
        block_id: 'va_mortgage_calc',
        block_displayed_txt: 'HOA dues'
      }
    },
    onChange: function onChange() {
      return handleFieldChangeEvent('HOA dues', 'form_field');
    }
  })), /*#__PURE__*/React.createElement(LegalPopover, {
    triggerText: "Calculator disclaimer",
    text: "The VA mortgage calculator is intended to be used for educational purposes only. Actual available rates and monthly payment amounts are subject to market fluctuations and will depend on a number of factors, including geography and loan characteristics. The estimates are based on information you provide, and may not include other fees and costs that a lender may assess in addition to monthly principal and interest, such VA funding fee, other taxes and insurance,and the actual payment obligation may be greater. Zillow Group Marketplace, Inc. does not make loans and this is not a commitment to lend.",
    interactionEventCmsInfo: legalPopoverInteractionEventCmsInfo
  }));
}
function CurrentRatesButtonUrl() {
  var form = useFormContext();

  // Watch for changes and do side effects
  var lastValidFields = useRef(null);
  if (lastValidFields.current === null) {
    lastValidFields.current = form.getValues();
  }
  var mortgateRatesUrl = useCustomQuotesURL('mortgage-rates', {
    value: lastValidFields.current[FieldConfig.homePrice.name],
    down: lastValidFields.current[FieldConfig.downPayment.name],
    auto: true,
    zip: API.zipcode && API.zipcode.length === 5 ? API.zipcode : null,
    source: 'Z_Mortgage_Calc_rates'
  });
  return mortgateRatesUrl;
}

export { VALoanCalculatorFields as default };
